import { useQuery } from '@apollo/client/react';
import { useCompanyFeatures } from 'app/domains/companies';
import { getCustomMetadataPropertyFilter, ActiveFilter, activeFilterLabel, AllowedTeamsFilter, allowedTeamsFilterLabel, AllowedUsersFilter, allowedUsersFilterLabel, AutomaticRenewalFilter, automaticRenewalFilterLabel, ContractCreatorFilter, contractCreatorFilterLabel, ContractOwnerFilter, contractOwnerFilterLabel, CountryFilter, countryFilterLabel, CreatedAtFilter, createdAtFilterLabel, CurrencyFilter, currencyFilterLabel, DateSignedFilter, dateSignedFilterLabel, DeviateFromStandardFilter, deviateFromStandardFilterLabel, DiscountFilter, discountFilterLabel, DocumentCategoryFilter, documentCategoryFilterLabel, DocumentProjectsFilter, documentProjectsFilterLabel, DocumentStartDateFilter, documentStartDateFilterLabel, DocumentStatusFilter, documentStatusFilterLabel, DocumentTypeFilter, documentTypeFilterLabel, EsigningStatusFilter, esigningStatusFilterLabel, FeeAdjustmentClauseFilter, feeAdjustmentClauseFilterLabel, FilesystemPathFilter, fileSystemPathFilterLabel, GoverningLawFilter, governingLawFilterLabel, IndemnityFilter, indemnityFilterLabel, LiabilityCapFilter, liabilityCapFilterLabel, LiabilityCapScopeFilter, liabilityCapScopeFilterLabel, LimitationOfLiabilityFilter, limitationOfLiabilityFilterLabel, MaterialAgreementFilter, materialAgreementFilterLabel, NoticeDateFilter, noticeDateFilterLabel, NoticePeriodDaysFilter, noticePeriodDaysFilterLabel, NoticePeriodMonthsFilter, noticePeriodMonthsFilterLabel, OriginalTermMonthsFilter, originalTermMonthsFilterLabel, OriginalVersionStoredFilter, originalVersionStoredFilterLabel, OutsourcingFilter, outsourcingFilterLabel, PartiesFilter, partiesFilterLabel, PaymentTermsDaysFilter, paymentTermsDaysFilterLabel, PostTerminationObligationsFilter, postTerminationObligationsFilterLabel, ProbationaryPeriodExpirationDateFilter, probationaryPeriodExpirationDateFilterLabel, ProbationaryPeriodFilter, probationaryPeriodFilterLabel, ProcessingOfPersonalDataFilter, processingOfPersonalDataFilterLabel, RenewalTermMonthsFilter, renewalTermMonthsFilterLabel, SearchDocumentTypeFilter, searchDocumentTypeFilterLabel, SharedExternallyFilter, sharedExternallyFilterLabel, TemplateCategoryFilter, templateCategoryFilterLabel, TerminationDateFilter, terminationDateFilterLabel, TerminationForCauseFilter, terminationForCauseFilterLabel, TerminationForConvenienceFilter, terminationForConvenienceFilterLabel, TotalContractCostFilter, totalContractCostFilterLabel, TotalContractValueFilter, totalContractValueFilterLabel, UpdatedAtFilter, updatedAtFilterLabel, ContractRoleFilter, contractRoleFilterLabel, MarketingTrademarkUseFilter, marketingTrademarkUseFilterLabel, } from 'app/domains/insights';
import { DefaultSearchFiltersDocument } from './DefaultSearchFilters.gql';
const dmsToSearchName = (name) => `metadataDocument${name}`;
export function useGetDefaultSearchFilters() {
    const { data, error, loading: queryLoading } = useQuery(DefaultSearchFiltersDocument);
    const { isFeatureAvailable, loading: companyFeaturesLoading } = useCompanyFeatures();
    const isProjectsAvailable = isFeatureAvailable('projects');
    const loading = queryLoading || companyFeaturesLoading;
    /**
     * List of all available filters, both "system" like `updatedAt`, `categoryId` and
     * metadata filters like `metadataDocumentAutomaticRenewal`.
     */
    const commonFilters = [
        {
            id: 'createdByUserId',
            component: ContractCreatorFilter,
            name: contractCreatorFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'categoryId',
            component: TemplateCategoryFilter,
            name: templateCategoryFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentType',
            component: SearchDocumentTypeFilter,
            name: searchDocumentTypeFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentStatus',
            component: DocumentStatusFilter,
            name: documentStatusFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentActive',
            component: ActiveFilter,
            name: activeFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentAutomaticRenewal',
            component: AutomaticRenewalFilter,
            name: automaticRenewalFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentDeviateFromStandard',
            component: DeviateFromStandardFilter,
            name: deviateFromStandardFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentMaterialAgreement',
            component: MaterialAgreementFilter,
            name: materialAgreementFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentOutsourcing',
            component: OutsourcingFilter,
            name: outsourcingFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentStartDate',
            component: DocumentStartDateFilter,
            name: documentStartDateFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'createdAt',
            component: CreatedAtFilter,
            name: createdAtFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'updatedAt',
            component: UpdatedAtFilter,
            name: updatedAtFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentDateSigned',
            component: DateSignedFilter,
            name: dateSignedFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentNoticeDate',
            component: NoticeDateFilter,
            name: noticeDateFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentTerminationDate',
            component: TerminationDateFilter,
            name: terminationDateFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentContractOwner',
            component: ContractOwnerFilter,
            name: contractOwnerFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentParties',
            component: PartiesFilter,
            name: partiesFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentCurrency',
            component: CurrencyFilter,
            name: currencyFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentDocumentCategory',
            component: DocumentCategoryFilter,
            name: documentCategoryFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentDocumentType',
            component: DocumentTypeFilter,
            name: documentTypeFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentEsigningStatus',
            component: EsigningStatusFilter,
            name: esigningStatusFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentGoverningLaw',
            component: GoverningLawFilter,
            name: governingLawFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentCountry',
            component: CountryFilter,
            name: countryFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentIsSharedExternally',
            component: SharedExternallyFilter,
            name: sharedExternallyFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentNoticePeriodDays',
            component: NoticePeriodDaysFilter,
            name: noticePeriodDaysFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentNoticePeriodMonths',
            component: NoticePeriodMonthsFilter,
            name: noticePeriodMonthsFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentOriginalTermMonths',
            component: OriginalTermMonthsFilter,
            name: originalTermMonthsFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentOriginalVersionStored',
            component: OriginalVersionStoredFilter,
            name: originalVersionStoredFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentRenewalTermMonths',
            component: RenewalTermMonthsFilter,
            name: renewalTermMonthsFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentTotalContractCost',
            component: TotalContractCostFilter,
            name: totalContractCostFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentTotalContractValue',
            component: TotalContractValueFilter,
            name: totalContractValueFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentProcessingOfPersonalData',
            component: ProcessingOfPersonalDataFilter,
            name: processingOfPersonalDataFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentLiabilityCapScope',
            component: LiabilityCapScopeFilter,
            name: liabilityCapScopeFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentDiscount',
            component: DiscountFilter,
            name: discountFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentFeeAdjustmentClause',
            component: FeeAdjustmentClauseFilter,
            name: feeAdjustmentClauseFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentIndemnity',
            component: IndemnityFilter,
            name: indemnityFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentLiabilityCap',
            component: LiabilityCapFilter,
            name: liabilityCapFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentLimitationOfLiability',
            component: LimitationOfLiabilityFilter,
            name: limitationOfLiabilityFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentPaymentTermsDaysFromInvoiceDate',
            component: PaymentTermsDaysFilter,
            name: paymentTermsDaysFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentPostTerminationObligations',
            component: PostTerminationObligationsFilter,
            name: postTerminationObligationsFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentProbationaryPeriod',
            component: ProbationaryPeriodFilter,
            name: probationaryPeriodFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentProbationaryPeriodExpirationDate',
            component: ProbationaryPeriodExpirationDateFilter,
            name: probationaryPeriodExpirationDateFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentTerminationForCause',
            component: TerminationForCauseFilter,
            name: terminationForCauseFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentTerminationForConvenience',
            component: TerminationForConvenienceFilter,
            name: terminationForConvenienceFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentProjectIds',
            component: DocumentProjectsFilter,
            name: documentProjectsFilterLabel(),
            type: 'common',
            exclude: !isProjectsAvailable,
        },
        {
            id: 'metadataEntryPath',
            component: FilesystemPathFilter,
            name: fileSystemPathFilterLabel(),
            type: 'common',
            exclude: true,
        },
        {
            id: 'allowedUsers',
            component: AllowedUsersFilter,
            name: allowedUsersFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'allowedTeams',
            component: AllowedTeamsFilter,
            name: allowedTeamsFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentContractRole',
            component: ContractRoleFilter,
            name: contractRoleFilterLabel(),
            type: 'common',
            exclude: false,
        },
        {
            id: 'metadataDocumentMarketingTrademarkUse',
            component: MarketingTrademarkUseFilter,
            name: marketingTrademarkUseFilterLabel(),
            type: 'common',
            exclude: false,
        },
    ];
    const filterItems = (data === null || data === void 0 ? void 0 : data.documentMetadataProperties.reduce((acc, property) => {
        const isCustomProperty = Boolean(property.companyId);
        if (isCustomProperty) {
            const customFilterComponent = getCustomMetadataPropertyFilter({
                name: property.name,
                searchName: dmsToSearchName(property.name),
                label: property.label,
                type: property.type,
                reference: property.reference,
                allowedValues: property.allowedValues,
            });
            // Some metadata properties are not treated as filters, and will not have any filter component.
            // For example text properties, they are search modifiers and not handled here.
            if (!customFilterComponent) {
                return acc;
            }
            const customFilter = {
                id: dmsToSearchName(property.name),
                name: property.label,
                type: 'custom',
                component: () => customFilterComponent,
            };
            return [...acc, customFilter];
        }
        const { isHidden } = property;
        const filter = commonFilters.find((f) => f.id === dmsToSearchName(property.name));
        if (!filter) {
            return acc;
        }
        return [
            ...acc,
            Object.assign(Object.assign({}, filter), { exclude: isHidden }),
        ];
    }, [])) || [];
    return {
        filterItems,
        loading,
        error,
    };
}
